'use client';

import UiIcon from '@/components/ui/UiIcon';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import Link from 'next/link';
import { apiClient, Organization, OrganizationType, Project } from '@/lib/api';
import Badge from '@/components/ui/Badge';

// Типы слоёв карты
export type MapLayerType = 'universities' | 'institutes' | 'departments' | 'projects' | 'project_detail';

// Уровни слоёв (1-5)
export const LAYER_LEVELS: Record<MapLayerType, number> = {
  universities: 1,
  institutes: 2,
  departments: 3,
  projects: 4,
  project_detail: 5,
};

// Названия слоёв
export const LAYER_NAMES: Record<MapLayerType, string> = {
  universities: 'Университеты и колледжи',
  institutes: 'Институты',
  departments: 'Кафедры и лаборатории',
  projects: 'Проекты',
  project_detail: 'Детали проекта',
};

// Интерфейс узла карты
export interface MapNode {
  id: string;
  name: string;
  type: MapLayerType;
  parentId?: string;
  coverUrl?: string;
  description?: string;
  projectData?: Project;
  children?: MapNode[];
  color?: string;
  icon?: string;
}

// Функция поиска узла по id
function findNodeById(nodes: MapNode[], id: string): MapNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

// Функция получения пути к узлу
function getPathToNode(nodes: MapNode[], targetId: string, path: MapNode[] = []): MapNode[] | null {
  for (const node of nodes) {
    if (node.id === targetId) return [...path, node];
    if (node.children) {
      const found = getPathToNode(node.children, targetId, [...path, node]);
      if (found) return found;
    }
  }
  return null;
}

interface OrganizationMapProps {
  projects: Project[];
  onProjectClick?: (project: Project) => void;
}

function orgTypeToLayer(t: OrganizationType): MapLayerType {
  if (t === 'institute') return 'institutes';
  if (t === 'department' || t === 'laboratory' || t === 'school' || t === 'gymnasium') return 'departments';
  // university / college / organization
  return 'universities';
}

function projectToNode(p: Project): MapNode {
  return {
    id: `project_${p.id}`,
    name: p.name,
    type: 'projects',
    description: p.description,
    coverUrl: p.cover_url || undefined,
    projectData: p,
    color: '#3B82F6',
    icon: '📁',
  };
}

function orgToNode(o: Organization, projectsByOrgId: Map<string, Project[]>): MapNode {
  const layer = orgTypeToLayer(o.type);
  const children: MapNode[] = [];
  if (Array.isArray(o.children) && o.children.length) {
    for (const ch of o.children) children.push(orgToNode(ch, projectsByOrgId));
  }
  const ps = projectsByOrgId.get(String(o.id)) || [];
  for (const p of ps) children.push(projectToNode(p));
  return {
    id: `org_${o.id}`,
    name: o.short_name || o.name,
    type: layer,
    description: o.description || undefined,
    coverUrl: o.cover_url || undefined,
    color: o.color || '#3B82F6',
    icon: o.icon || undefined,
    children: children.length ? children : undefined,
  };
}

export default function OrganizationMap({ projects }: OrganizationMapProps) {
  const [currentLayer, setCurrentLayer] = useState<MapLayerType>('universities');
  const [currentParentId, setCurrentParentId] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [navigationHistory, setNavigationHistory] = useState<{ layer: MapLayerType; parentId: string | null }[]>([]);
  const [animating, setAnimating] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const [orgTree, setOrgTree] = useState<MapNode[]>([]);
  const [orgLoading, setOrgLoading] = useState<boolean>(false);
  const [orgError, setOrgError] = useState<string | null>(null);

  // Комментарий на русском: грузим реальную структуру организаций и подмешиваем проекты по organization_id.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOrgLoading(true);
      setOrgError(null);
      try {
        const projectsByOrgId = new Map<string, Project[]>();
        for (const p of projects) {
          const orgIdRaw = (p as any)?.organization_id as number | string | null | undefined;
          if (orgIdRaw === null || orgIdRaw === undefined || orgIdRaw === '') continue;
          // Комментарий на русском: id организации из API может быть строкой.
          const orgId = String(orgIdRaw);
          const arr = projectsByOrgId.get(orgId) || [];
          arr.push(p);
          projectsByOrgId.set(orgId, arr);
        }

        const res = await apiClient.getOrganizations({ flat: false, active_only: true });
        if (!res?.success) throw new Error('Не удалось загрузить организации');
        const tree = (res as any).data as Organization[];
        const nodes = tree.map((o) => orgToNode(o, projectsByOrgId));

        if (!cancelled) {
          setOrgTree(nodes);
        }
      } catch (e: any) {
        if (!cancelled) setOrgError(e?.message || 'Ошибка загрузки организаций');
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projects]);

  const organizationsWithProjects = orgTree;

  // Получение текущих узлов для отображения
  const currentNodes = useMemo(() => {
    if (currentLayer === 'universities') {
      return organizationsWithProjects.filter((n) => n.type === 'universities');
    }
    if (currentParentId) {
      const parent = findNodeById(organizationsWithProjects, currentParentId);
      return parent?.children || [];
    }
    return [];
  }, [currentLayer, currentParentId, organizationsWithProjects]);

  // Получение текущего родителя
  const currentParent = useMemo(() => {
    if (!currentParentId) return null;
    return findNodeById(organizationsWithProjects, currentParentId);
  }, [currentParentId, organizationsWithProjects]);

  // Хлебные крошки
  const breadcrumbs = useMemo(() => {
    if (!currentParentId) return [{ id: null, name: 'Карта', layer: 'universities' as MapLayerType }];
    const path = getPathToNode(organizationsWithProjects, currentParentId);
    if (!path) return [{ id: null, name: 'Карта', layer: 'universities' as MapLayerType }];
    return [
      { id: null, name: 'Карта', layer: 'universities' as MapLayerType },
      ...path.map((n) => ({ id: n.id, name: n.name, layer: n.type })),
    ];
  }, [currentParentId, organizationsWithProjects]);

  // Анимация перехода
  const triggerAnimation = useCallback(() => {
    setAnimating(true);
    setAnimationKey((k) => k + 1);
    setTimeout(() => setAnimating(false), 300);
  }, []);

  // Переход на следующий уровень
  const navigateToNode = useCallback((node: MapNode) => {
    // Клик на проект - открываем детальный вид
    if (node.type === 'projects' && node.projectData) {
      triggerAnimation();
      setSelectedProject(node.projectData);
      setNavigationHistory((prev) => [...prev, { layer: currentLayer, parentId: currentParentId }]);
      setCurrentLayer('project_detail');
      return;
    }

    // Клик на организацию - всегда переходим внутрь (даже если нет детей)
    // Определяем следующий слой на основе типа детей или показываем "projects" если детей нет
    triggerAnimation();
    setNavigationHistory((prev) => [...prev, { layer: currentLayer, parentId: currentParentId }]);
    const nextLayer = node.children?.[0]?.type || 'projects';
    setCurrentLayer(nextLayer);
    setCurrentParentId(node.id);
  }, [currentLayer, currentParentId, triggerAnimation]);

  // Назад
  const navigateBack = useCallback(() => {
    if (navigationHistory.length === 0) return;
    triggerAnimation();
    const prev = navigationHistory[navigationHistory.length - 1];
    setNavigationHistory((h) => h.slice(0, -1));
    setCurrentLayer(prev.layer);
    setCurrentParentId(prev.parentId);
    setSelectedProject(null);
  }, [navigationHistory, triggerAnimation]);

  // Переход по хлебным крошкам
  const navigateToBreadcrumb = useCallback((index: number) => {
    triggerAnimation();
    if (index === 0) {
      setCurrentLayer('universities');
      setCurrentParentId(null);
      setSelectedProject(null);
      setNavigationHistory([]);
      return;
    }
    const bc = breadcrumbs[index];
    if (bc) {
      const historySlice = navigationHistory.slice(0, index);
      setNavigationHistory(historySlice);
      setCurrentLayer(bc.layer);
      setCurrentParentId(bc.id);
      setSelectedProject(null);
    }
  }, [breadcrumbs, navigationHistory, triggerAnimation]);

  // Клавиши навигации
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Backspace') {
        if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
        e.preventDefault();
        navigateBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigateBack]);

  // Карточка узла
  const NodeCard = ({ node, index }: { node: MapNode; index: number }) => {
    const isProject = node.type === 'projects';
    const hasChildren = node.children && node.children.length > 0;

    return (
      <div
        onClick={() => navigateToNode(node)}
        className="group cursor-pointer animate-fadeIn"
        style={{ animationDelay: `${index * 50}ms` }}
      >
        <div
          className="relative rounded-2xl border border-site-border bg-site-bg/60 backdrop-blur-sm overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:border-site-border-control/60"
          style={{ boxShadow: `0 0 30px ${node.color}20` }}
        >
          {/* Обложка */}
          <div className="relative h-32 bg-gradient-to-br from-site-surface to-site-bg overflow-hidden">
            {node.coverUrl ? (
              <img
                src={node.coverUrl}
                alt={node.name}
                className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center text-5xl opacity-60 group-hover:opacity-100 transition-opacity"
                style={{ background: `linear-gradient(135deg, ${node.color}30, transparent)` }}
              >
                {<UiIcon name={node.icon || '📁'} />}
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-site-bg via-transparent to-transparent" />
            {hasChildren && (
              <div className="absolute top-2 right-2">
                <Badge variant="secondary" size="sm" className="bg-site-surface/80">
                  {node.children!.length}
                </Badge>
              </div>
            )}
          </div>

          {/* Контент */}
          <div className="p-4">
            <h3 className="text-site-text font-semibold text-lg truncate group-hover:text-site-accent transition-colors">
              {node.name}
            </h3>
            {node.description && (
              <p className="text-site-muted text-sm mt-1 line-clamp-2">{node.description}</p>
            )}

            {/* Прогресс для проектов */}
            {isProject && node.projectData && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-site-muted mb-1">
                  <span>Прогресс</span>
                  <span>{node.projectData.progress ?? 0}%</span>
                </div>
                <div className="h-1.5 bg-site-surface rounded-full overflow-hidden">
                  <div
                    className="h-full bg-site-progress"
                    style={{ width: `${Math.max(0, Math.min(100, node.projectData.progress ?? 0))}%` }}
                  />
                </div>
              </div>
            )}

            {/* Стрелка навигации */}
            <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-site-text"
                style={{ background: node.color }}
              >
                →
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Детали проекта
  const ProjectDetail = ({ project }: { project: Project }) => (
    <div className="max-w-4xl mx-auto animate-fadeIn">
      <div className="rounded-2xl border border-site-border bg-site-bg/70 backdrop-blur-sm overflow-hidden">
        {/* Обложка проекта */}
        <div className="relative h-48 bg-site-action/10">
          {project.cover_url ? (
            <img
              src={project.cover_url}
              alt={project.name}
              className="w-full h-full object-cover opacity-70"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-7xl opacity-40"><UiIcon name="Folder" /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-site-bg via-site-bg/50 to-transparent" />
        </div>

        <div className="p-6 -mt-16 relative z-10">
          <h2 className="text-3xl font-bold text-site-text mb-2">{project.name}</h2>
          {project.description && <p className="text-site-secondary text-lg mb-4">{project.description}</p>}

          {/* Метаданные */}
          <div className="flex flex-wrap gap-3 mb-6">
            {project.status && (
              <Badge variant={project.status === 'active' ? 'success' : 'secondary'}>{project.status}</Badge>
            )}
            {project.scientific_field && <Badge variant="secondary">{project.scientific_field}</Badge>}
            <Badge variant="secondary"><UiIcon name="Eye" /> {project.views_count ?? 0}</Badge>
          </div>

          {/* Прогресс */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-site-muted mb-2">
              <span>Уровень реализации</span>
              <span className="text-site-text font-medium">{project.progress ?? 0}%</span>
            </div>
            <div className="h-3 bg-site-surface rounded-full overflow-hidden">
              <div
                className="h-full bg-site-progress transition-all duration-500"
                style={{ width: `${project.progress ?? 0}%` }}
              />
            </div>
          </div>

          {/* Кнопка перехода */}
          <Link
            href={`/projects/${project.id}`}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-site-accent hover:bg-site-accent text-site-on-accent font-medium transition-colors"
          >
            Открыть проект →
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className="min-h-[600px]">
      {/* CSS для анимаций */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
          opacity: 0;
        }
      `}</style>

      {/* Хлебные крошки и навигация */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {navigationHistory.length > 0 && (
          <button
            onClick={navigateBack}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-site-surface/60 border border-site-border text-site-secondary hover:text-site-text hover:bg-site-raised/60 transition-all"
          >
            ← Назад
          </button>
        )}

        <div className="flex items-center gap-1 text-sm">
          {breadcrumbs.map((bc, i) => (
            <div key={bc.id ?? 'root'} className="flex items-center">
              {i > 0 && <span className="mx-2 text-site-muted">/</span>}
              <button
                onClick={() => navigateToBreadcrumb(i)}
                className={`px-2 py-1 rounded-lg transition-colors ${
                  i === breadcrumbs.length - 1
                    ? 'text-site-text font-medium'
                    : 'text-site-muted hover:text-site-text hover:bg-site-surface/40'
                }`}
              >
                {bc.name}
              </button>
            </div>
          ))}
          {selectedProject && (
            <>
              <span className="mx-2 text-site-muted">/</span>
              <span className="text-site-text font-medium">{selectedProject.name}</span>
            </>
          )}
        </div>
      </div>

      {/* Заголовок слоя */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-site-text">
          {currentLayer === 'project_detail' ? 'Проект' : currentParent?.name || LAYER_NAMES[currentLayer]}
        </h2>
        <p className="text-site-muted mt-1">
          {currentLayer === 'project_detail'
            ? 'Подробная информация'
            : `Уровень ${LAYER_LEVELS[currentLayer]}: ${LAYER_NAMES[currentLayer]}`}
        </p>
      </div>

      {/* Контент */}
      <div key={animationKey} className={animating ? 'opacity-0' : ''}>
        {orgLoading ? (
          <div className="py-12 text-center text-site-muted">Загрузка организаций…</div>
        ) : orgError ? (
          <div className="py-12 text-center">
            <div className="text-site-danger mb-2">{orgError}</div>
            <div className="text-site-muted text-sm">Проверьте, что миграции 073/074 применены и API доступен.</div>
          </div>
        ) : null}
        {currentLayer === 'project_detail' && selectedProject ? (
          <ProjectDetail project={selectedProject} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {currentNodes.length > 0 ? (
              currentNodes.map((node, i) => <NodeCard key={node.id} node={node} index={i} />)
            ) : (
              <div className="col-span-full text-center py-16 animate-fadeIn">
                <div className="text-6xl mb-4 opacity-40"><UiIcon name="EnvelopeOpen" /></div>
                <p className="text-site-muted text-lg">Нет элементов на этом уровне</p>
                {navigationHistory.length > 0 && (
                  <button
                    onClick={navigateBack}
                    className="mt-4 px-4 py-2 rounded-lg bg-site-surface/60 border border-site-border text-site-secondary hover:text-site-text transition-colors"
                  >
                    ← Вернуться назад
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Подсказка */}
      <div className="mt-8 text-center text-site-muted text-sm">
        <kbd className="px-2 py-1 rounded bg-site-surface border border-site-border text-xs">Click</kbd>
        {' '}— перейти внутрь • {' '}
        <kbd className="px-2 py-1 rounded bg-site-surface border border-site-border text-xs">Esc</kbd>
        {' '}— назад
      </div>
    </div>
  );
}
